import { Link } from "react-router-dom";
import { Trash } from "@phosphor-icons/react";
import { Avatar, RoleChip, relTime } from "./_shared";

// WhatsApp-style animated 3 dots for the "peer is typing" hint on the
// conversation card. Kept inline (no separate component) because it's
// only ever rendered in this one place and the CSS is 6 lines. The
// bouncing keyframes come from tailwind's built-in `animate-bounce` —
// we just stagger the delays so the three dots don't move in lockstep.
function TypingDotsInline() {
  return (
    <span
      className="inline-flex items-center gap-[3px]"
      aria-label="يَكتُب الآن"
      data-testid="conversation-typing-dots"
    >
      <span className="w-[5px] h-[5px] rounded-full bg-[#2E8B57] animate-bounce" style={{ animationDelay: "0ms",   animationDuration: "1s" }} />
      <span className="w-[5px] h-[5px] rounded-full bg-[#2E8B57] animate-bounce" style={{ animationDelay: "150ms", animationDuration: "1s" }} />
      <span className="w-[5px] h-[5px] rounded-full bg-[#2E8B57] animate-bounce" style={{ animationDelay: "300ms", animationDuration: "1s" }} />
    </span>
  );
}

// One conversation card — glass row with avatar / title / role chip /
// preview / unread pill + destructive delete side-button.
export default function ConversationRow({ conv, myId, isTyping, onSelect, onRequestDelete }) {
  const isGroup = conv.kind === "group";
  const title = isGroup ? (conv.title || "محادثة جماعية") : (conv.counterpart?.name || "مستخدم");
  const subtitle = conv.last_message_preview || "ابدأ المُحادثة…";
  const sender = conv.last_sender_id === myId ? "أنت: " : "";
  return (
    <li className="bg-white/55 backdrop-blur-md rounded-[22px] border border-white/60 shadow-[0_2px_12px_rgba(10,25,47,0.06)] overflow-hidden">
      <div className="flex items-stretch">
        <Link
          to={`/messages/${conv.id}`}
          onClick={() => onSelect(conv)}
          className="flex items-center gap-3 flex-1 min-w-0 px-4 py-3 active:bg-white/40 transition"
          data-testid={`messages-row-${conv.id}`}
        >
          <Avatar name={title} size={50} avatarId={isGroup ? "" : (conv.counterpart?.avatar_id || "")} />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-bold text-[14.5px] text-[#0A192F] truncate">{title}</span>
                {!isGroup && <RoleChip role={conv.counterpart?.role} />}
              </div>
              <span className="text-[11px] text-[#8A8A8A] shrink-0">{relTime(conv.last_message_at)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 mt-0.5">
              {isTyping ? (
                <span className="flex items-center gap-1.5 text-[12.5px] font-bold text-[#2E8B57]">
                  <TypingDotsInline />
                  <span>يَكتُب الآن…</span>
                </span>
              ) : (
                <span className={`text-[12.5px] truncate ${conv.unread > 0 ? "text-[#0A192F] font-bold" : "text-[#5A6470]"}`}>
                  {sender}{subtitle}
                </span>
              )}
              {conv.unread > 0 && (
                <span
                  className="shrink-0 bg-[#10B981] text-white text-[10.5px] font-bold px-2 py-0.5 rounded-full min-w-[20px] text-center shadow-[0_2px_6px_rgba(16,185,129,0.4)]"
                  data-testid={`messages-unread-${conv.id}`}
                >
                  {conv.unread > 99 ? "99+" : conv.unread}
                </span>
              )}
            </div>
          </div>
        </Link>
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRequestDelete(conv); }}
          className="w-12 shrink-0 flex items-center justify-center text-[#5A6470] hover:text-[#E0413B] active:text-[#E0413B] active:bg-[#E0413B]/10 transition border-r border-white/60"
          aria-label="حذف المحادثة"
          data-testid={`messages-delete-${conv.id}`}
        >
          <Trash size={18} weight="bold" />
        </button>
      </div>
    </li>
  );
}
